import { FormInstance } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { NouveauDossierConcessionnaireData } from 'types/Concessionnaire';

interface UseStep2PersonnePhysiqueFlowArgs {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  personnePhysique: boolean;
  commandeSectionFields: (keyof NouveauDossierConcessionnaireData)[];
  questionsSectionFields: (keyof NouveauDossierConcessionnaireData)[];
}

export default function useStep2PersonnePhysiqueFlow({
  form,
  personnePhysique,
  commandeSectionFields,
  questionsSectionFields,
}: UseStep2PersonnePhysiqueFlowArgs) {
  const [isCommandeSectionOpen, setIsCommandeSectionOpen] = useState(true);
  const [isQuestionsSectionOpen, setIsQuestionsSectionOpen] = useState(false);
  const [isCommandeSectionSaved, setIsCommandeSectionSaved] = useState(false);
  const [isQuestionsSectionSaved, setIsQuestionsSectionSaved] = useState(false);

  useEffect(() => {
    if (!personnePhysique) {
      setIsCommandeSectionOpen(true);
      setIsQuestionsSectionOpen(true);
      setIsCommandeSectionSaved(true);
      setIsQuestionsSectionSaved(true);
      return;
    }

    setIsCommandeSectionOpen(true);
    setIsQuestionsSectionOpen(false);
    setIsCommandeSectionSaved(false);
    setIsQuestionsSectionSaved(false);
  }, [personnePhysique]);

  const handleSaveCommandeSection = useCallback(async () => {
    try {
      await form.validateFields(commandeSectionFields as string[]);
    } catch {
      return;
    }

    setIsCommandeSectionSaved(true);
    setIsCommandeSectionOpen(false);
    setIsQuestionsSectionOpen(true);
  }, [commandeSectionFields, form]);

  const handleSaveQuestionsSection = useCallback(async () => {
    try {
      await form.validateFields(questionsSectionFields as string[]);
    } catch {
      return;
    }

    setIsQuestionsSectionSaved(true);
    setIsQuestionsSectionOpen(false);
  }, [form, questionsSectionFields]);

  const handleValuesChange = useCallback(
    (changedValues: Partial<NouveauDossierConcessionnaireData>) => {
      if (!personnePhysique) {
        return;
      }

      const changedKeys = Object.keys(
        changedValues,
      ) as (keyof NouveauDossierConcessionnaireData)[];

      const commandeTouched = changedKeys.some((key) =>
        commandeSectionFields.includes(key),
      );
      const questionsTouched = changedKeys.some((key) =>
        questionsSectionFields.includes(key),
      );

      if (commandeTouched && isCommandeSectionSaved) {
        setIsCommandeSectionSaved(false);
        setIsQuestionsSectionSaved(false);
        setIsCommandeSectionOpen(true);
        setIsQuestionsSectionOpen(false);
      }

      if (questionsTouched && isQuestionsSectionSaved) {
        setIsQuestionsSectionSaved(false);
      }
    },
    [
      commandeSectionFields,
      isCommandeSectionSaved,
      isQuestionsSectionSaved,
      personnePhysique,
      questionsSectionFields,
    ],
  );

  const toggleCommandeSection = useCallback(() => {
    setIsCommandeSectionOpen((previous) => !previous);
  }, []);

  const toggleQuestionsSection = useCallback(() => {
    setIsQuestionsSectionOpen((previous) => !previous);
  }, []);

  return {
    isCommandeSectionOpen,
    isQuestionsSectionOpen,
    isCommandeSectionSaved,
    isQuestionsSectionSaved,
    handleSaveCommandeSection,
    handleSaveQuestionsSection,
    handleValuesChange,
    toggleCommandeSection,
    toggleQuestionsSection,
  };
}
